import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils.ts';

// shadcn/ui Button בשפת המותג — יעד מגע 44px בכל הגדלים (11 §4).
// default = laser (פעולה ראשית); secondary = משטח מורם; outline/ghost; destructive; soft = רקע primary-soft.
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm font-medium transition-[background-color,border-color,color,box-shadow,transform] duration-150 disabled:pointer-events-none disabled:opacity-50 min-h-11 min-w-11 cursor-pointer select-none active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
  {
    variants: {
      variant: {
        default: 'bg-action text-action-foreground hover:bg-action-hover',
        secondary: 'bg-muted text-foreground hover:bg-raised',
        outline: 'border border-border bg-card text-foreground hover:border-primary/50 hover:bg-muted',
        ghost: 'text-muted-foreground hover:bg-muted hover:text-foreground',
        soft: 'bg-primary-soft text-primary hover:bg-primary-soft/70',
        destructive: 'border border-error/50 bg-error/10 text-error hover:bg-error/15',
      },
      size: { default: 'px-4 py-2 text-[15px]', sm: 'px-3 text-sm', lg: 'px-6 text-base', icon: 'p-0 size-11' },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, ...props }, ref) => (
  <button ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
));
Button.displayName = 'Button';
